import { useState } from 'react';

export default function CTASection() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    situation: '',
    timeline: '',
    transactionalConsent: false,
    marketingConsent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://readdy.ai/api/form/submit/avoid-foreclosure-contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          address: formData.address,
          situation: formData.situation,
          timeline: formData.timeline,
          transactionalConsent: formData.transactionalConsent ? 'Yes' : 'No',
          marketingConsent: formData.marketingConsent ? 'Yes' : 'No',
        }).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ 
          name: '', 
          phone: '', 
          email: '', 
          address: '', 
          situation: '', 
          timeline: '',
          transactionalConsent: false,
          marketingConsent: false,
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-block bg-red-100 text-red-800 px-4 py-2 rounded-full text-sm font-bold mb-6">
              <i className="ri-alarm-warning-fill mr-1"></i>
              Time Is Running Out
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 leading-tight">
              Get Your Free Cash Offer Today and Avoid Foreclosure
            </h2>
            <p className="text-xl text-gray-700 mb-8 leading-relaxed">
              Don't wait until it's too late. Contact us now for a free, no-obligation cash offer on your El Paso home. We can close in as little as 7 days and help you avoid foreclosure, protect your credit, and move forward with cash in hand.
            </p>

            <div className="space-y-6 mb-8">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-phone-fill text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Call Us Now</h3>
                  <a href="tel:+1-915-206-5814" className="text-2xl font-black text-teal-600 hover:text-teal-700 cursor-pointer">
                    (915) 206-5814
                  </a>
                  <p className="text-sm text-gray-600 mt-1">Available 7 days a week</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-mail-fill text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Email Us</h3>
                  <a href="mailto:ElPaso@trexrealestate.com" className="text-lg text-teal-600 hover:text-teal-700 cursor-pointer">
                    ElPaso@trexrealestate.com
                  </a>
                  <p className="text-sm text-gray-600 mt-1">We respond within 1 hour</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-map-pin-fill text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Visit Our Office</h3>
                  <p className="text-base text-gray-700">
                    3016 Gull Lake Pl<br />
                    El Paso, TX 79936
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-lg font-bold text-gray-900 mb-4">What Happens Next?</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1 flex-shrink-0"></i>
                  <span className="text-base text-gray-700">We'll contact you within 1 hour to discuss your situation</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1 flex-shrink-0"></i>
                  <span className="text-base text-gray-700">You'll receive a fair cash offer within 24 hours</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1 flex-shrink-0"></i>
                  <span className="text-base text-gray-700">We can close in 7 days and help you avoid foreclosure</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <h3 className="text-2xl font-black text-gray-900 mb-6">Request Your Free Cash Offer</h3>
            <form onSubmit={handleSubmit} data-readdy-form id="avoid-foreclosure-contact">
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none text-base"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none text-base"
                    placeholder="(915) 555-0100"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none text-base"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="address" className="block text-sm font-semibold text-gray-900 mb-2">
                    Property Address *
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none text-base"
                    placeholder="123 Main Street, El Paso, TX 79901"
                  />
                </div>

                <div>
                  <label htmlFor="timeline" className="block text-sm font-semibold text-gray-900 mb-2">
                    Foreclosure Timeline *
                  </label>
                  <select
                    id="timeline"
                    name="timeline"
                    value={formData.timeline}
                    onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none text-base"
                  >
                    <option value="">Select your situation</option>
                    <option value="behind-payments">Behind on payments, no notice yet</option>
                    <option value="notice-default">Received Notice of Default</option>
                    <option value="notice-sale">Received Notice of Trustee Sale</option>
                    <option value="sale-scheduled">Foreclosure sale scheduled</option>
                    <option value="not-sure">Not sure / Need help understanding</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="situation" className="block text-sm font-semibold text-gray-900 mb-2">
                    Tell Us About Your Situation
                  </label>
                  <textarea
                    id="situation"
                    name="situation"
                    value={formData.situation}
                    onChange={(e) => setFormData({ ...formData, situation: e.target.value })}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none text-base resize-none"
                    placeholder="Briefly describe your situation and how we can help..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>

                <div className="space-y-3 pt-2">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="foreclosure-transactionalConsent"
                      name="transactionalConsent"
                      required
                      checked={formData.transactionalConsent}
                      onChange={(e) => setFormData({ ...formData, transactionalConsent: e.target.checked })}
                      className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                    />
                    <label htmlFor="foreclosure-transactionalConsent" className="ml-3 text-xs text-gray-600 leading-relaxed cursor-pointer">
                      <span className="text-red-500">*</span> I consent to receive transactional messages from TREX RE LLC to the phone number provided. Message frequency may vary. Message and Data rates may apply. Reply HELP for help or STOP to opt-out.
                    </label>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="foreclosure-marketingConsent"
                      name="marketingConsent"
                      checked={formData.marketingConsent}
                      onChange={(e) => setFormData({ ...formData, marketingConsent: e.target.checked })}
                      className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                    />
                    <label htmlFor="foreclosure-marketingConsent" className="ml-3 text-xs text-gray-600 leading-relaxed cursor-pointer">
                      I consent to receive marketing and promotional messages from TREX RE LLC at the phone number provided. Message frequency may vary. Message and Data rates may apply. Reply HELP for help or STOP to opt-out.
                    </label>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg text-lg font-bold transition-all shadow-lg cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill mr-2"></i>
                      Get My Free Cash Offer
                    </>
                  )}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border-2 border-green-600 rounded-lg p-4 text-center">
                    <i className="ri-checkbox-circle-fill text-green-600 text-2xl mb-2"></i>
                    <p className="text-base font-semibold text-green-800">
                      Thank you! We'll contact you within 1 hour.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border-2 border-red-600 rounded-lg p-4 text-center">
                    <i className="ri-error-warning-fill text-red-600 text-2xl mb-2"></i>
                    <p className="text-base font-semibold text-red-800">
                      Something went wrong. Please call us at (915) 206-5814.
                    </p>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}